(ns dashboard.entity.analysis.profile
  (:require ["react" :as react]
            [clojure.data :as data]
            [clojure.string :as str]
            [dashboard.common :as common]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.utils :as utils]))

;; Common utils
(defn diff-report [applied-analyses-outdated]
  (str "Applied analysis is outdated for this structured analysis. Please update the applied analysis, if possible."
       "\n\nThere are changes in fields:\n\n"
       (str/join "\n" (map (fn [[k v]]
                             (str (name k) ": " (if (vector? v)
                                                  (str "added/changed "
                                                       (let [added (remove nil? v)]
                                                         (case k
                                                           :states (str/join ", " (map #(str "'" (second %) "'") added))
                                                           :categories (str/join ", " (map #(str "'" (:name %) "'") added))
                                                           added)))
                                                  (str "change to: " (case k
                                                                       :state-dependencies "New state dependency added"
                                                                       v)))))
                           applied-analyses-outdated))))

(defn latest-applied-outdated
  "Returns true if the latest applied analysis does not point to the latest structured analysis.
  Note that if there are no applied analysis, will also return true.
  ** Note that this doc-string is incorrect and needs to be updated:
  This function does NOT return booleans!"
  [{:keys [applied-analyses] :as structured-analysis}]
  (let [{:keys [structured-analysis-version-uuid] :as _latest-applied-analysis} (first applied-analyses)]
    (and (not= structured-analysis-version-uuid (:version-id structured-analysis))
         ;; TODO: Sequential values should exclude position in the sequence, report addition/removal.
         ;;       Now for example selecting a category that is prior in the list of old selection, will show both old and new in the diff.
         ;;       The report should show added and removed items.
         (let [a (first (data/diff (dissoc structured-analysis :previous-versions :applied-analyses :tree/ancestors :tree/children :tree/depth :author)
                                   (first (filter #(= structured-analysis-version-uuid (:version-id %))
                                                  (:previous-versions structured-analysis)))))
               a (dissoc a :structured-analysis-id :created :previous-version-id :next-version-id :version-id)]
           a))))

(defn format-analysis-id [construct-id]
  (.padStart (str construct-id) 3 "0"))

(defn get-state-by-id [states id]
  (->> states
       (filter (fn [state]
                 (= (first state) id)))
       first))

(defn search-query-tree-filter [search-query {:keys [applied-analyses] :as structured-analysis}]
  (utils/match-search-query
   search-query
   (fn [query-word]
     (let [latest-applied-analysis (first applied-analyses)]
       (or (utils/str-includes-icase? (:construct structured-analysis) query-word)
           (utils/str-includes-icase? (-> structured-analysis :construct-id format-analysis-id) query-word)
           (when latest-applied-analysis
             (let [{:keys [states]} structured-analysis
                   state-id->state-name (into {} states)
                   selected-state-name (state-id->state-name (:state-id latest-applied-analysis) "")]
               (utils/str-includes-icase? selected-state-name query-word)))
           (utils/str-includes-icase? (:evidence-search-text latest-applied-analysis)
                                      query-word)
           (some #(utils/str-includes-icase? (:name %) query-word)
                 (:categories structured-analysis))
           (some #(utils/str-includes-icase? % query-word)
                 (:relates-to structured-analysis))
           (some #(utils/str-includes-icase? % query-word)
                 (:excludes structured-analysis)))))))

;; Common components

(defn field-header
  [text]
  [:div.title.mb-1
   [:label.label.is-size-5 text]])

(defn field
  [label & components]
  (into [:div.field.mt-2.mb-5
         [field-header label]]
        components))

(defn subfield-header
  [text]
  [:div.title.mb-1
   [:label.label.is-size-6 text]])

(defn subfield
  [label & components]
  (into [:div.field.mt-2.mb-4
         [subfield-header label]]
        components))

(defn subsection
  [& args]
  (into [:div.analysis-list-subsection.p-2.mt-4] args))

(defn compare-diff
  "Accepts an old and new structure and presents a diff in a popup menu for
   data about a single field/key"
  [old new key-fn & [display-name]]
  (when (not= (key-fn old)
              (key-fn new))
    (let [fill-in-blanks (fn [coll ikey-fn]
                           [:b (if (empty? (ikey-fn coll))
                                 "<<blank>>"
                                 (str (ikey-fn coll)))])]
      [:f> popup-menu/popup-menu
       {:trigger-class [:button
                        :is-small
                        :is-warning
                        :applied-structued-analysis-field-diff-trigger]
        :trigger-content [:span.icon.is-small.>i.fas.fa-solid.fa-exclamation]
        :disabled false
        :content [:div.applied-structued-analysis-field-diff.notification.is-warning
                  [:div
                   [:h4 "Change to Analysis Structure"]
                   "The " (or display-name (name key-fn)) " field"
                   " has changed from "]
                  [fill-in-blanks old key-fn]
                  [:div " to "]
                  [fill-in-blanks new key-fn]]}])))

(defn structure-card-header-title
  [construct-id construct version-or-construct-version diff]
  [:span.is-size-4.card-header-title.has-text-weight-normal
   {:style {:display "inline"}}
   [:span.mr-4
    [:b (str "Analysis " (format-analysis-id construct-id))]
    " (v" version-or-construct-version ")"]
   construct
   " "
   diff])

(defn card-header
  [construct-id construct version-or-construct-version on-close diff categories state]
  [:div.panel.section.pl-5.pr-3.py-3
   [:div.level.is-flex.is-narrow.is-info.px-0.m-0.pb-2
    [:div [:b categories] state]
    [:div.level
     [:div.mr-4
      [:span.mr-2
       (str "Analysis " (format-analysis-id construct-id) " ")]
      " (v" version-or-construct-version ") "]
     [:button.button.ml-2
      {:on-click on-close}
      [:span.icon [:i.fas.fa-xmark]]]]]
   [:header
    [:div
     [:div.card-header-title.my-0.p-0.level
      [:div [:b.title.is-3.pr-3 construct] diff]]]]])

(defn state-name->icon
  [state-name]
  (condp contains? (str/lower-case (or state-name ""))
    #{"yes" "true"} [:i.icon.fas.fa-circle-check.mr-0]
    #{"no" "false"} [:i.icon.fas.fa-circle-xmark.mr-0]
    #{"needs more information"} [:i.icon.fas.fa-file-circle-question.mr-0]
    #{"out of scope"} [:i.icon.fas.fa-square-minus.fa-solid.mr-0]
    [:i.icon.fas.fa-circle-question.is-info.mr-0]))

(defn state-name->class
  [state-name]
  (condp contains? (str/lower-case (or state-name ""))
    #{"yes" "true"} ["is-success" "is-true"]
    #{"no" "false"} ["is-false"]
    #{"needs more information"} ["is-warning" "is-light" "is-needs-more-information"]
    #{"out of scope"} ["is-out-of-scope"]
    ["is-not-yet-evaluated"]))

(defn state-tag
  ([state]
   (state-tag {} state))
  ([{:keys [class] :as props} state]
   (let [state-name (cond
                      (vector? state) (second state)
                      (string? state) state)]
     [:span.tag (->> (concat (if (vector? class)
                               class
                               (vector class))
                             (state-name->class state-name))
                     (assoc props :class))
      [state-name->icon state-name]
      state-name])))

(defn view-button
  ([on-click]
   (view-button on-click "is-info" nil))
  ([on-click class label]
   [:button.button.is-rounded.is-small.is-secondary
    {:title "View analysis"
     :on-click on-click
     :class class}
    "View"
    (when label (str " " label))]))

(defn expandable-row-header
  [text & buttons]
  [:div.level
   [:div.analysis-expandable-row text]
   (into [:div.buttons] buttons)])

(defn version-history [all-versions current-version-id on-view]
  (let [[view-table set-view-table] (react/useState false)]
    [field
     [expandable-row-header
      (str "Analysis versions ("
           (count all-versions)
           ")")
      [:button.button
       {:on-click #(set-view-table not)
        :title "Show or hide the hierarchy of applied analysis"}
       (if view-table
         [:i.icon.fas.fa-caret-up]
         [:i.icon.fas.fa-caret-down])]]
     (when view-table
       (->> all-versions
            (map-indexed
             (fn [index {:keys [version
                                construct-version
                                version-id
                                author
                                created] :as row}]
               #^{:key (str "version-" (or version construct-version))}
                 (let [version (or version construct-version)]
                   [:<>
                    (when (> index 0) [:hr.my-0])
                    [:div.level.py-2.my-0.px-3
                     {:class (when (= version-id current-version-id)
                               :active-row)}
                     [:div.level-left
                      [:div.mr-2 (str "v" version)]
                      [:div.mr-2 author]
                      [:div (common/format-date-time created)]]
                     [:div.level-right>div
                      (if (= version-id current-version-id)
                        [:span.is-size-7 "You are currently viewing this analysis"]
                        [view-button #(on-view row)])]]])))
            (into [subsection])
            vector
            (into [:div.analysis-list])))]))

(defn state-dependency
  [{:keys [relative-to child-states parent-states dependency clickable]}]
  (let [[parent-state-id child-state-id] dependency
        child-state (get-state-by-id child-states child-state-id)
        parent-state (get-state-by-id parent-states parent-state-id)

        from (case relative-to
               :parent "parent "
               :child "this ")
        to (case relative-to
             :parent "this "
             :child "sub-")]
    [:div.level.analysis-list-subrow.p-2.my-0
     {:key (str "state-dependency-" child-state-id "-to-" parent-state-id)
      :class (when clickable :clickable-row)}
     [:div.level-left.mb1
      [:span.mr-2 "If " [:b from "analysis'"] " state is"]
      [state-tag {:class ["is-small" "mr-2"]} parent-state]
      [:span.mr-2 "then " [:b to "analysis'"] " state should be"]
      [state-tag {:class "is-small"} child-state]]]))

(defn dependancy-line-item
  [{:keys [construct-id construct]
    :as sub-analysis}
   show-dependancies
   on-view-child
   on-show-dependancies]
  [:div.level.my-0
   [:a.analysis-list-row.clickable-row.is-flex-grow-1.px-2
    {:href "#"
     :on-click (fn [e]
                 (.preventDefault e)
                 (on-view-child construct-id))}
    [:span
     [:span.has-text-info.mr-2.is-size-7 (format-analysis-id construct-id)]
     (when (:parent-id sub-analysis) [:div.mr-2.mt-2.branch-icon])
     construct]]
   (when (ifn? on-show-dependancies)
     [:button.button.is-rounded.is-small.ml-2
      {:title "View analysis dependancies"
       :on-click #(if-not (= construct-id show-dependancies)
                    (on-show-dependancies construct-id)
                    (on-show-dependancies nil))
       :class (when (= construct-id show-dependancies)
                "is-active")}
      "Dependancies"])])

(defn parent-dependencies-view
  [{:keys [states]
    :as parent-analysis}
   child-analysis
   on-view-parent]
  (let [[view-table set-view-table] (react/useState false)
        [show-dependancies set-show-dependancies] (react/useState nil)
        dependancies (:state-dependencies child-analysis)]
    [field
     [expandable-row-header
      "Analysis depends on"
      [:button.button
       {:on-click #(set-view-table not)
        :title "Show or hide the children of this applied analysis"}
       (if view-table
         [:i.icon.fas.fa-caret-up]
         [:i.icon.fas.fa-caret-down])]]
     (when view-table
       [subsection
        [dependancy-line-item
         parent-analysis
         show-dependancies
         on-view-parent
         (when (seq dependancies) set-show-dependancies)]
        (when (and (seq dependancies)
                   (= (:construct-id parent-analysis)
                      show-dependancies))
          (into [:div.analysis-list.section.analysis-list-subsection]
                (map #(state-dependency
                       {:relative-to :parent
                        :child-states (:states child-analysis)
                        :parent-states states
                        :dependency %})
                     (:state-dependencies child-analysis))))])]))

(defn child-dependencies-view [sub-analyses parent-states on-view-child]
  (let [[view-table set-view-table] (react/useState false)
        [show-dependancies set-show-dependancies] (react/useState nil)]
    [field
     [expandable-row-header
      "Analysis informs sub-analysis"
      [:button.button
       {:on-click #(set-view-table not)
        :title "Show or hide the children of this applied analysis"}
       (if view-table
         [:i.icon.fas.fa-caret-up]
         [:i.icon.fas.fa-caret-down])]]
     (when view-table
       (->> sub-analyses
            (map (fn [{:keys [construct-id] :as sub-analysis}]
                   (let [sub-states (:states sub-analysis)
                         sub-state-dependencies (:state-dependencies sub-analysis)]
                     [:div.my-0.py-0.px-0.analysis-list-row
                      {:key (str "sub-analysis-" construct-id)}
                      [dependancy-line-item
                       sub-analysis
                       show-dependancies
                       on-view-child
                       (when (seq sub-state-dependencies)
                         set-show-dependancies)]
                      (when (and (seq sub-state-dependencies)
                                 (= construct-id show-dependancies))
                        (->> sub-state-dependencies
                             (map #(state-dependency
                                    {:relative-to :child
                                     :child-states sub-states
                                     :parent-states parent-states
                                     :dependency %}))
                             (into [subsection])
                             vector
                             (into [:div.mx-4.pb-4])))])))
            subsection
            vector
            (into [:div.analysis-list])))]))

(defn categories-tags [categories]
  (->> categories
       (map (fn [{:keys [id name]}]
              #^{:key (str "category-" id)}
                [:span.is-medium.mr-2.tag.is-category name]))
       (into [:<>])))

(defn categories-view [categories]
  [field
   "Analysis categories"
   [categories-tags categories]])

(defn expandable-hierarchy-table
  "Draws the hierarchy of constructs of either a structure or applied analysis
  lineage"
  [relative-to-analysis-id row-class action-component on-click get-hierarchy]
  (let [[ids-to-include update-ids-to-include] (react/useState #{})
        [view-full-hierarchy set-view-full-hierarchy] (react/useState false)
        [view-table set-view-table] (react/useState false)

        hierarchy (when view-table @(get-hierarchy relative-to-analysis-id))
        {source-ancestors :tree/ancestors} (->> hierarchy
                                                (filter (comp (partial = relative-to-analysis-id)
                                                              :construct-id))
                                                first)
        progenator-id (->> hierarchy
                           (filter (comp source-ancestors :construct-id))
                           (filter (comp nil? :parent-id))
                           first
                           :construct-id)
        original-ancestory (disj source-ancestors progenator-id)]

    (react/useEffect
     (fn []
       (update-ids-to-include original-ancestory))
     (array view-full-hierarchy view-table))

    [field
     [expandable-row-header
      "Analysis hierarchy"
      [:button.button
       {:on-click #(set-view-table not)
        :title "Show or hide the hierarchy of applied analysis"}
       (if view-table
         [:i.icon.fas.fa-caret-up]
         [:i.icon.fas.fa-caret-down])]
      [:button.button
       {:disabled (not view-table)
        :title "Expand all nodes or show the path to the current analysis"
        :on-click #(set-view-full-hierarchy not)}
       (if view-full-hierarchy
         [:i.icon.fas.fa-minimize]
         [:i.icon.fas.fa-expand])]]
     (when view-table
       (->> hierarchy
            (map
             (fn [{:keys [construct-id construct parent-id]
                   :tree/keys [depth ancestors children]
                   :as structured-analysis}]
               (when (or (<= depth 1)
                         view-full-hierarchy
                         (contains? ids-to-include parent-id)
                         (contains? ancestors relative-to-analysis-id))
                 (let [active-construct (= relative-to-analysis-id construct-id)]
                   [(if active-construct
                      :div.level.my-0.analysis-list-row.mx-0
                      :a.level.my-1.analysis-list-row.mx-0)
                    {:key (str "hierarchy-" construct-id)
                     :tab-index (if-not active-construct 0 -1)
                     :href "#"
                     :on-click (fn [e]
                                 (.preventDefault e)
                                 (when-not active-construct
                                   (on-click structured-analysis)))
                     :class [:hierarchy-row
                             (when-not active-construct :clickable-row)
                             (when active-construct :active-row)
                             (when (fn? row-class) (row-class structured-analysis))]}
                    [:div.level.my-0
                     [:div.has-text-info.mr-2.is-size-7
                      {:class (when (or (zero? depth)
                                        (contains? original-ancestory construct-id)
                                        (= construct-id relative-to-analysis-id))
                                "has-text-weight-bold")}
                      (format-analysis-id construct-id)]
                     [:div.level.my-0 {:style {:padding-left (str (* (dec depth) 36) "px")}}
                      (when (pos? depth) [:div.mr-2.my-0.branch-icon])
                      [:span
                       {:class (when (or (zero? depth)
                                         (contains? original-ancestory construct-id)
                                         (= construct-id relative-to-analysis-id))
                                 "has-text-weight-bold")}
                       construct]
                      (when (not (or (empty? children)
                                     (contains? ancestors relative-to-analysis-id)
                                     (contains? source-ancestors construct-id)
                                     active-construct
                                     view-full-hierarchy))
                        [:button.button.is-small.is-rounded.is-circular.ml-2.is-light
                         {:on-click (fn [e]
                                      (.stopPropagation e)
                                      (let [new-ids (apply conj original-ancestory ancestors)]
                                        (update-ids-to-include
                                         (if (contains? ids-to-include construct-id)
                                           new-ids
                                           (conj new-ids construct-id)))))}
                         (if (contains? ids-to-include construct-id)
                           [:i.icon.fas.fa-chevron-up]
                           [:i.icon.fas.fa-chevron-down])])]]
                    (if active-construct
                      [:div.ml-1.mr-2.is-size-7
                       [:b "You are currently viewing this analysis"]]
                      [action-component structured-analysis])]))))
            (into [:div.analysis-list])
            vector
            (into [subsection])))]))


(defn StateSetBy
  [editor-state]
  [field
   "State set by"
   (case (:state-set-by editor-state)
     "this" "Validated by analyst for this analysis"
     "dependency" "Validated by analyst via a dependency"
     "")])

(defn ConstructStateTag [structured-analysis selected-state-id]
  (let [state-id->state (into {} (:states structured-analysis))]
    [state-tag (get state-id->state selected-state-id)]))

(defn Product [product-value on-change mode]
  [field
   "Client product this analysis applies to"
   [:input.input
    {:title "The product that the analysis relates to"
     :disabled (not= :edit mode)
     :type "text"
     :value product-value
     :auto-complete "asa-product"
     :on-change (fn [e]
                  (on-change (.. e -target -value)))}]])

(defn state-editor-radio
  [structured-analysis selected-state-id editor-state set-editor-state]
  [:div.column.is-narrow.level
   (for [[state-id state-name] (:states structured-analysis)]
     [:<> {:key state-id}
      [:input.ml-1
       {:id (str "state-" state-id)
        :type "radio"
        :name "state"
        :checked (= selected-state-id state-id)
        :on-change (fn [_]
                     (-> editor-state
                         (update :state
                                 assoc
                                 :state-id state-id
                                 :state-set-by "this")
                         set-editor-state))}]
      [:label.ml-2.mr-5
       {:for (str "state-" state-id)
        :class (state-name->class state-name)}
       [state-name->icon state-name]
       state-name]])])
